import { useState } from 'react'
import { Upload, Building2, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { NodeAvatar } from '@/components/nodes/NodeAvatar'
import { toast } from 'sonner'
import { simulateNetworkDelay } from '@/services/storage'
import { cn } from '@/lib/utils/cn'
import type { User as UserType, Workspace } from '@/types/nodes'

/**
 * Node types that support image uploads
 */
export type UploadableNode = UserType | Workspace

/**
 * Size variants for the image display
 */
export type ImageSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

/**
 * Layout variants for the upload component
 */
export type ImageLayout = 'horizontal' | 'vertical'

/**
 * Props for the NodeImage component
 */
export interface NodeImageProps {
  /**
   * The node to display image for
   */
  node: UploadableNode
  /**
   * Size variant for the image (defaults to 'md')
   */
  size?: ImageSize
  /**
   * Layout variant for the component (defaults to 'horizontal')
   */
  layout?: ImageLayout
  /**
   * Callback when image is uploaded/changed
   */
  onImageChange?: (imageUrl: string) => void
  /**
   * Callback when file is selected (for custom handling)
   */
  onFileSelect?: (file: File) => void
  /**
   * Whether the upload is disabled
   */
  disabled?: boolean
  /**
   * Custom className for the container
   */
  className?: string
  /**
   * Custom upload button text
   */
  uploadText?: string
  /**
   * Whether to show file size limit text
   */
  showFileInfo?: boolean
  /**
   * Maximum file size in MB (defaults to 5)
   */
  maxFileSizeMB?: number
  /**
   * Whether to simulate network delay during upload
   */
  simulateDelay?: boolean
}

/**
 * Maps size variants to avatar sizes
 */
const sizeMap: Record<ImageSize, 'sm' | 'md' | 'lg' | 'xl' | 'xxl'> = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xl: 'xl',
  xxl: 'xxl',
}

/**
 * Gets the appropriate fallback icon for different node types
 */
function getFallbackIcon(node: UploadableNode) {
  if ('avatar' in node) {
    // User node
    return <User className="h-6 w-6" />
  }

  if ('logo' in node) {
    // Workspace node
    return <Building2 className="h-6 w-6" />
  }

  return <User className="h-6 w-6" />
}

/**
 * Gets the current image URL for the node
 */
function getCurrentImage(node: UploadableNode): string | undefined {
  if ('avatar' in node) {
    return node.avatar
  }

  if ('logo' in node) {
    return node.logo
  }

  return undefined
}

/**
 * A flexible component for displaying and uploading node images
 * Supports both User and Workspace nodes with different layouts and sizes
 *
 * @example
 * ```tsx
 * // Basic user avatar upload
 * <NodeImage
 *   node={user}
 *   onImageChange={(url) => updateUserAvatar(url)}
 * />
 *
 * // Workspace logo upload with vertical layout
 * <NodeImage
 *   node={workspace}
 *   layout="vertical"
 *   size="lg"
 *   onImageChange={(url) => updateWorkspaceLogo(url)}
 * />
 *
 * // Custom file handling
 * <NodeImage
 *   node={user}
 *   onFileSelect={(file) => customUploadHandler(file)}
 * />
 * ```
 */
export function NodeImage({
  node,
  size = 'md',
  layout = 'horizontal',
  onImageChange,
  onFileSelect,
  disabled = false,
  className,
  uploadText,
  showFileInfo = true,
  maxFileSizeMB = 5,
  simulateDelay = true,
}: NodeImageProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [preview, setPreview] = useState<string | undefined>(getCurrentImage(node))

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    // Validate file size
    const maxSizeBytes = maxFileSizeMB * 1024 * 1024
    if (file.size > maxSizeBytes) {
      toast.error(`Image size must be less than ${maxFileSizeMB}MB`)
      return
    }

    setIsUploading(true)

    try {
      // Simulate upload delay if enabled
      if (simulateDelay) {
        await simulateNetworkDelay()
      }

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        const result = reader.result as string
        setPreview(result)

        // Call the appropriate callback
        if (onImageChange) {
          onImageChange(result)
        }

        if (onFileSelect) {
          onFileSelect(file)
        }

        toast.success('Image uploaded successfully')
      }
      reader.readAsDataURL(file)
    } catch {
      toast.error('Failed to upload image')
    } finally {
      setIsUploading(false)
    }
  }

  const avatarSize = sizeMap[size]
  const fallbackIcon = getFallbackIcon(node)

  // Determine upload button text
  const defaultUploadText = uploadText || 'Upload Image'
  const buttonText = isUploading ? 'Uploading...' : defaultUploadText

  // Create a mock node with the preview for the avatar
  const avatarNode = {
    ...node,
    ...('avatar' in node ? { avatar: preview } : { logo: preview }),
  } as UploadableNode

  if (layout === 'vertical') {
    return (
      <div className={cn('flex flex-col items-center gap-4', className)}>
        <NodeAvatar node={avatarNode} size={avatarSize} fallback={fallbackIcon} />

        <div className="relative">
          <input
            type="file"
            id={`image-upload-${node.id}`}
            className="sr-only"
            accept="image/*"
            onChange={handleFileChange}
            disabled={disabled || isUploading}
          />
          <label htmlFor={`image-upload-${node.id}`}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              asChild
              disabled={disabled || isUploading}
            >
              <span className="cursor-pointer">
                <Upload className="mr-2 h-4 w-4" />
                {buttonText}
              </span>
            </Button>
          </label>
          {showFileInfo && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              PNG, JPG or GIF (max. {maxFileSizeMB}MB)
            </p>
          )}
        </div>
      </div>
    )
  }

  // Horizontal layout (default)
  return (
    <div className={cn('flex items-center gap-4', className)}>
      <NodeAvatar node={avatarNode} size={avatarSize} fallback={fallbackIcon} />

      <div className="flex-1">
        <input
          type="file"
          id={`image-upload-${node.id}`}
          className="sr-only"
          accept="image/*"
          onChange={handleFileChange}
          disabled={disabled || isUploading}
        />
        <label htmlFor={`image-upload-${node.id}`}>
          <Button type="button" variant="outline" asChild disabled={disabled || isUploading}>
            <span className="cursor-pointer">
              <Upload className="mr-2 h-4 w-4" />
              {buttonText}
            </span>
          </Button>
        </label>
        {showFileInfo && (
          <p className="text-xs text-muted-foreground mt-2">
            PNG, JPG or GIF (max. {maxFileSizeMB}MB)
          </p>
        )}
      </div>
    </div>
  )
}
